import { createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../store";
import type { 
  UserExtended,
  FormExtended,
  TagExtended,
  FormThemeExtended,
  QuestionComment
} from "effno-core";
import { QUESTION_SETTINGS_TABS } from "../../../components/question-settings/QuestionSettings";
import { FormThemeSettings } from "../../../components/question-design/QuestionDesign";

export type uiStatesType = {
  searchOpen: boolean;
  formSettingsOpen: boolean;
  questionPickerOpen: boolean;
  designSettingsOpen: boolean;
  viewingBuild: boolean;
  viewingDesign: boolean;
  viewingSettings: boolean;
  viewingPreview: boolean;
  viewingComments: boolean;
  viewingResponses: boolean;
  viewingShare: boolean;
  mobileLayout: boolean
  questionSettingsTab: number;
  commentMode: boolean;
  editingFormTheme: boolean;
  viewingComment: boolean;
  mediaManagerOpen: boolean;
  openingDesign: boolean;
};

export type AppDataState = {
  user: UserExtended | undefined;
  token: string | undefined;
  signUpNow: boolean;
  selectedMenu: string | undefined;
  selectedSubMenu: string | undefined;
  selectedMenuId: string | undefined;
  memberAdded: boolean;
  uiStates: uiStatesType;
  isMobile: boolean;
  forms: FormExtended[] | undefined;
  tags: TagExtended[] | undefined;
  formThemes: FormThemeExtended[] | undefined;
  editingForm: FormExtended | undefined; 
  editingQuestionId: string | undefined;
  viewingFormThemeSettings: FormThemeSettings | undefined;
  selectedComment: QuestionComment | undefined;
};

const initialState = {
  user: undefined,
  token: undefined,
  trackerData: undefined,
  signUpNow: false,
  selectedMenu: "unselected",
  selectedSubMenu: "unselected",
  selectedMenuId: undefined,
  memberAdded: false,
  uiStates: {
    searchOpen: false,
    formSettingsOpen: false,
    questionPickerOpen: false,
    designSettingsOpen: false,
    viewingBuild: true,
    viewingDesign: false,
    viewingSettings: false,
    viewingPreview: false,
    viewingComments: false,
    viewingResponses: false,
    viewingShare: false,
    mobileLayout: false,
    questionSettingsTab: QUESTION_SETTINGS_TABS.SETTINGS,
    commentMode: false,
    editingFormTheme: false,
    viewingComment: false,
    mediaManagerOpen: false,
    openingDesign: false,
  },
  isMobile: false,
  forms: undefined,
  tags: undefined,
  formThemes: undefined,
  editingForm: undefined,
  editingQuestionId: undefined,
  viewingFormThemeSettings: undefined,
  selectedComment: undefined,
} as AppDataState;

export const appDataSlice = createSlice({
  name: "appData",
  initialState,
  reducers: {
    setUser: (state, action) => {
      state.user = action.payload;
    },
    clearUser: (state) => {
      state.user = undefined;
    },
    setToken: (state, action) => {
      state.token = action.payload;
    },
    clearToken: (state) => {
      state.token = undefined;
    },
    setSignUpNow: (state, action) => {
      state.signUpNow = action.payload;
    },
    setSelectedMenu: (state, action) => {
      state.selectedMenu = action.payload;
    },
    setSelectedSubMenu: (state, action) => {
      state.selectedSubMenu = action.payload;
    },
    setSelectedMenuId: (state, action) => {
      state.selectedMenuId = action.payload;
    },
    setUIStates: (state, action) => {
      state.uiStates = { ...action.payload };
    },
    setMemberAdded: (state, action) => {
      state.memberAdded = action.payload;
    },
    setIsMobile: (state, action) => {
      state.isMobile = action.payload;
    },
    setForms: (state, action) => {
      state.forms = action.payload;
    },
    setTags: (state, action) => {
      state.tags = action.payload;
    },
    setFormThemes: (state, action) => {
      state.formThemes = action.payload;
    },
    setEditingForm: (state, action) => {
      state.editingForm = action.payload;
    },
    setEditingQuestionId: (state, action) => {
      state.editingQuestionId = action.payload;
    },
    setViewingFormThemeSettings: (state, action) => {
      state.viewingFormThemeSettings = action.payload;
    },
    setSelectedComment: (state, action) => {
      state.selectedComment = action.payload;
    },
  },
});

export const {
  setUser,
  clearUser,
  setToken,
  clearToken,
  setSignUpNow,
  setSelectedMenu,
  setSelectedSubMenu,
  setSelectedMenuId,
  setUIStates,
  setMemberAdded,
  setIsMobile,
  setForms,
  setTags,
  setFormThemes,
  setEditingForm,
  setEditingQuestionId,
  setViewingFormThemeSettings,
  setSelectedComment
} = appDataSlice.actions;

// Other code such as selectors can use the imported `RootState` type
export const selectUser = (state: RootState) => state.appData.user;
export const selectToken = (state: RootState) => state.appData.token;
export const selectSignupNow = (state: RootState) => state.appData.signUpNow;
export const selectSelectedMenu = (state: RootState) =>
  state.appData.selectedMenu;
export const selectSelectedSubMenu = (state: RootState) =>
  state.appData.selectedSubMenu;
export const selectSelectedMenuId = (state: RootState) =>
  state.appData.selectedMenuId;
export const selectUIStates = (state: RootState) => state.appData.uiStates;
export const selectMemberAdded = (state: RootState) =>
  state.appData.memberAdded;
export const selectIsMobile = (state: RootState) => state.appData.isMobile;
export const selectForms = (state: RootState) => state.appData.forms;
export const selectTags = (state: RootState) => state.appData.tags;
export const selectFormThemes = (state: RootState) => state.appData.formThemes;
export const selectEditingForm = (state: RootState) => state.appData.editingForm;
export const selectEditingQuestionId = (state: RootState) => state.appData.editingQuestionId;
export const selectViewingFormThemeSettings = (state: RootState) => state.appData.viewingFormThemeSettings;
export const selectSelectedComment = (state: RootState) => state.appData.selectedComment;

export default appDataSlice.reducer;
